import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BookOpen, Award, Users, Briefcase, Calendar, Settings, Menu, X, Check, Loader2, Mail, RefreshCw, MessageSquare, ExternalLink, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { interviewApi, interviewerApi } from '../../services/api';
import AppliedFilters from '../../components/UI/AppliedFilters';
import { confirmAction } from '../../utils/confirmAction';
import { InterviewRequestItem, InterviewerItem } from '../../types';
import { INTERVIEW_TECHNOLOGIES } from '../../content/interviewsPage';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import Button from '../../components/UI/Button';
import ActionStatus from '../../components/UI/ActionStatus';
import Card from '../../components/UI/Card';
import { API_PATHS, apiHeaders, apiUrl } from '../../config/api';
import { formatDate } from '../../utils/formatDate';
import Input from '../../components/UI/Input';
import { Course } from '../../types';
import { contentApi } from '../../services/contentApi';
import { isAcademyAdmin, isOwner as userIsOwner, hasRole } from '../../utils/roles';

const STATUS_OPTIONS = ['Pending', 'Active', 'Cancelled'];

export type SchoolAdminTab = 'adminstats' | 'programs' | 'enrollments' | 'users' | 'gigs' | 'events' | 'settings' | 'inquiries' | 'interviews' | 'emails';

interface SchoolAdminPanelProps {
  embedded?: boolean;
  tab?: SchoolAdminTab;
}

const SchoolAdminPanel: React.FC<SchoolAdminPanelProps> = ({ embedded = false, tab }) => {
  const { user, token, logout } = useAuth();
  const isOwner = userIsOwner(user);
  const canManagePrograms = isOwner || isAcademyAdmin(user) || hasRole(user, 'courseadmin');
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => window.innerWidth >= 1024);
  const sidebarRef = useRef<HTMLDivElement>(null);

  // Detect screen size and update sidebar open state
  useEffect(() => {
    if (embedded) return;
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsSidebarOpen(true);
      } else {
        setIsSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Lock body scroll when sidebar is open on mobile
  useEffect(() => {
    if (embedded) return;
    if (window.innerWidth < 1024 && isSidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isSidebarOpen]);

  // Focus trap for sidebar on mobile
  useEffect(() => {
    if (embedded || !isSidebarOpen || window.innerWidth >= 1024) return;
    const focusableSelectors = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    const sidebar = sidebarRef.current;
    if (!sidebar) return;
    const focusableEls = sidebar.querySelectorAll<HTMLElement>(focusableSelectors);
    if (focusableEls.length === 0) return;
    const firstEl = focusableEls[0];
    const lastEl = focusableEls[focusableEls.length - 1];
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstEl) {
            e.preventDefault();
            lastEl.focus();
          }
        } else {
          if (document.activeElement === lastEl) {
            e.preventDefault();
            firstEl.focus();
          }
        }
      } else if (e.key === 'Escape') {
        setIsSidebarOpen(false);
      }
    };
    sidebar.addEventListener('keydown', handleKeyDown);
    firstEl.focus();
    return () => sidebar.removeEventListener('keydown', handleKeyDown);
  }, [isSidebarOpen]);

  const toggleSidebar = () => setIsSidebarOpen((open) => !open);

  const [activeTab, setActiveTab] = useState<SchoolAdminTab>(tab || 'programs');

  useEffect(() => {
    if (tab) setActiveTab(tab);
  }, [tab]);

  // Programs state
  const [courses, setCourses] = useState<Course[]>([]);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [errorCourses, setErrorCourses] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<Partial<Course>>({});
  const [editingId, setEditingId] = useState<number | null>(null);
  const [programFormMessage, setProgramFormMessage] = useState<string | null>(null);
  const [programFormError, setProgramFormError] = useState<string | null>(null);
  const [aiTopic, setAiTopic] = useState('');
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiMessage, setAiMessage] = useState<string | null>(null);


  // Enrollments state
  const [enrollmentsByCourse, setEnrollmentsByCourse] = useState<Record<number, any[]>>({});
  const [loadingEnrollments, setLoadingEnrollments] = useState(false);
  const [selectedCourseId, setSelectedCourseId] = useState<number | null>(null);

  // Users state (mock for now)
  const [users, setUsers] = useState<any[]>([]);
  useEffect(() => {
    if (isOwner) {
      setUsers([
        { id: 1, name: 'Alice', email: 'alice@example.com', role: 'Student' },
        { id: 2, name: 'Bob', email: 'bob@example.com', role: 'Student' },
        { id: 3, name: 'Carol', email: 'carol@example.com', role: 'CourseAdmin' },
      ]);
    }
  }, [isOwner]);

  // User and course lookup state
  const [userDetails, setUserDetails] = useState<Record<number, any>>({});
  const [courseDetails, setCourseDetails] = useState<Record<number, any>>({});

  // Track status edits and saving state for enrollments
  const [statusEdits, setStatusEdits] = useState<Record<number, string>>({});
  const [savingStatus, setSavingStatus] = useState<Record<number, boolean>>({});
  const [savedStatus, setSavedStatus] = useState<Record<number, boolean>>({});

  // Add a loading state for user profile batch fetch
  const [loadingUserProfiles, setLoadingUserProfiles] = useState(false);

  // State for enrollments filter and sort
  const [enrollmentStatusFilter, setEnrollmentStatusFilter] = useState<string>('All');
  const [enrollmentSort, setEnrollmentSort] = useState<{ field: 'status' | 'enrollmentDate', direction: 'asc' | 'desc' }>({ field: 'enrollmentDate', direction: 'desc' });

  // Enrollment counts state
  const [enrollmentCounts, setEnrollmentCounts] = useState<Record<number, { active: number; pending: number; total: number }>>({});
  const [loadingCounts, setLoadingCounts] = useState(false);

  // Inquiries state
  const [inquiries, setInquiries] = useState<any[]>([]);
  const [inquiriesLoading, setInquiriesLoading] = useState(false);
  const [inquiriesError, setInquiriesError] = useState<string | null>(null);
  const [inquiriesPage, setInquiriesPage] = useState(1);
  const [inquiriesPageSize, setInquiriesPageSize] = useState(5);
  const [inquiriesTotal, setInquiriesTotal] = useState(0);
  const [replyingTo, setReplyingTo] = useState<number | null>(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [replyLoading, setReplyLoading] = useState(false);
  const [replySuccess, setReplySuccess] = useState<string | null>(null);
  const [replyError, setReplyError] = useState<string | null>(null);
  const replyInputRef = useRef<HTMLTextAreaElement>(null);

  // Email notifications state
  const [emailNotifications, setEmailNotifications] = useState<any[]>([]);
  const [emailNotificationsLoading, setEmailNotificationsLoading] = useState(false);
  const [emailNotificationsError, setEmailNotificationsError] = useState<string | null>(null);
  const [emailNotificationsPage, setEmailNotificationsPage] = useState(1);
  const [emailNotificationsPageSize, setEmailNotificationsPageSize] = useState(10);
  const [emailNotificationsTotal, setEmailNotificationsTotal] = useState(0);
  const [emailStatusFilter, setEmailStatusFilter] = useState<string>('Failed');
  const [retriggeringId, setRetriggeringId] = useState<number | null>(null);

  // Add state for meeting invite
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [meetingLink, setMeetingLink] = useState('');
  const [sessionDate, setSessionDate] = useState('');
  const [sessionTime, setSessionTime] = useState('');
  const [meetingType, setMeetingType] = useState<'Invite' | 'Reminder'>('Invite');
  const [sendingMeetingInvite, setSendingMeetingInvite] = useState(false);
  const [meetingInviteSuccess, setMeetingInviteSuccess] = useState<string | null>(null);
  const [meetingInviteError, setMeetingInviteError] = useState<string | null>(null);

  const [interviews, setInterviews] = useState<InterviewRequestItem[]>([]);
  const [loadingInterviews, setLoadingInterviews] = useState(false);
  const [interviewError, setInterviewError] = useState<string | null>(null);
  const [interviewStatusFilter, setInterviewStatusFilter] = useState('All');
  const [interviewTechFilter, setInterviewTechFilter] = useState('All');
  const [interviewInterviewerFilter, setInterviewInterviewerFilter] = useState('All');
  const [interviewDateSort, setInterviewDateSort] = useState<'asc' | 'desc'>('desc');
  const [interviewers, setInterviewers] = useState<InterviewerItem[]>([]);
  const [assigningId, setAssigningId] = useState<number | null>(null);
  const [rejectingInterview, setRejectingInterview] = useState<InterviewRequestItem | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [schedulingInterview, setSchedulingInterview] = useState<InterviewRequestItem | null>(null);
  const [interviewMeetingLink, setInterviewMeetingLink] = useState('');
  const [interviewDate, setInterviewDate] = useState('');
  const [interviewTime, setInterviewTime] = useState('');
  const [savingInterview, setSavingInterview] = useState(false);
  const [interviewScheduleSuccess, setInterviewScheduleSuccess] = useState<string | null>(null);
  const [feedbackDrafts, setFeedbackDrafts] = useState<Record<number, string>>({});
  const [savingFeedbackId, setSavingFeedbackId] = useState<number | null>(null);

  // Helper to fetch user details by userId (with caching)
  const fetchUserDetails = useCallback(async (userId: number) => {
    if (userDetails[userId]) return userDetails[userId];
    try {
      const response = await fetch(apiUrl(API_PATHS.userProfile.byIds), {
        method: 'POST',
        headers: apiHeaders(token, { 'Content-Type': 'application/json' }),
        body: JSON.stringify({ userProfileIds: [userId] }),
      });
      const data = await response.json();
      const profile = data.success && data.userProfiles?.[0];
      if (profile) {
        setUserDetails(prev => ({ ...prev, [userId]: profile }));
        return profile;
      }
    } catch {}
    return null;
  }, [userDetails, token]);

  // Helper to fetch user details by userIds (batch)
  const fetchUserDetailsBatch = useCallback(async (userIds: number[]) => {
    // Only fetch userIds not already in userDetails
    const idsToFetch = userIds.filter(id => !userDetails[id]);
    if (idsToFetch.length === 0) return;
    try {
      const response = await fetch(apiUrl(API_PATHS.userProfile.byIds), {
        method: 'POST',
        headers: apiHeaders(token, { 'Content-Type': 'application/json' }),
        body: JSON.stringify({ userProfileIds: idsToFetch }),
      });
      const data = await response.json();
      if (data.success && data.userProfiles) {
        const newDetails: Record<number, any> = {};
        for (const profile of data.userProfiles) {
          // Key only by UserProfile.Id. Never alias IDUserId — it can collide with
          // another profile's Id (e.g. Admin idUserId=1 vs LinkedIn profile id=1).
          newDetails[profile.id] = profile;
        }
        setUserDetails(prev => ({ ...prev, ...newDetails }));
      }
    } catch {}
  }, [userDetails, token]);

  // Helper to fetch course details by courseId (with caching)
  const fetchCourseDetails = useCallback(async (courseId: number) => {
    if (courseDetails[courseId]) return courseDetails[courseId];
    try {
      const response = await fetch(apiUrl(API_PATHS.courses.byId(courseId)), {
        headers: apiHeaders(),
      });
      const data = await response.json();
      if (data.success && data.course) {
        setCourseDetails(prev => ({ ...prev, [courseId]: data.course }));
        return data.course;
      }
    } catch {}
    return null;
  }, []);

  // Fetch all programs (courses)
  useEffect(() => {
    const fetchCourses = async () => {
      setLoadingCourses(true);
      setErrorCourses(null);
      try {
        const params = new URLSearchParams({
          enrollmentAllowedOnly: 'false',
        });
        const response = await fetch(`${apiUrl(API_PATHS.courses.list)}?${params.toString()}`, {
          headers: apiHeaders(token),
        });
        const data = await response.json();
        if (data.success) {
          setCourses(data.courses);
        } else {
          setErrorCourses(data.message || 'Failed to fetch programs');
        }
      } catch (err) {
        setErrorCourses('Failed to fetch programs. Please try again later.');
      } finally {
        setLoadingCourses(false);
      }
    };
    fetchCourses();
      }, [token]);

  // Fetch enrollment counts when courses are loaded
  useEffect(() => {
    if (courses.length > 0) {
      fetchEnrollmentCounts();
    }
  }, [courses]);

  // CRUD Handlers
  const handleEdit = (course: Course) => {
    setForm(course);
    setEditingId(course.id);
    setShowForm(true);
  };
  const handleDelete = async (id: number) => {
    const soft = await confirmAction({
      title: 'Deactivate this program?',
      description: 'Soft delete sets the program inactive. It will leave the public catalog but can be reactivated. Only the Owner can permanently delete.',
      confirmLabel: 'Deactivate',
      cancelLabel: 'Keep program',
      variant: 'danger',
    });
    if (!soft) return;
    try {
      const response = await fetch(apiUrl(API_PATHS.courses.delete(id)), {
        method: 'DELETE',
        headers: apiHeaders(token),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) {
        alert(data.message || 'Failed to deactivate program.');
        return;
      }
      setCourses((current) => current.map((c) => (c.id === id ? { ...c, isActive: false } : c)));
    } catch {
      alert('Failed to deactivate program.');
    }
  };

  const handlePermanentDelete = async (id: number) => {
    const ok = await confirmAction({
      title: 'Permanently delete this program?',
      description: 'This removes the program record. Only the platform Owner can do this. Prefer deactivate when possible.',
      confirmLabel: 'Delete permanently',
      cancelLabel: 'Cancel',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      const response = await fetch(apiUrl(API_PATHS.courses.deletePermanent(id)), {
        method: 'DELETE',
        headers: apiHeaders(token),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) {
        alert(data.message || 'Permanent delete failed.');
        return;
      }
      setCourses((current) => current.filter((c) => c.id !== id));
    } catch {
      alert('Permanent delete failed.');
    }
  };
  const handleAdd = () => {
    setForm({
      trainingMode: 'Online',
      language: 'English',
      duration: '6 Months',
      fee: 0,
      discount: 0,
      maxParticipants: 50,
      isActive: true,
      content: JSON.stringify({ program: { title: '', duration: '6 Months', programHighlights: [], learningJourney: [], outcomes: [], projects: [] } }, null, 2),
      courseMaterials: '[]',
      tools: '',
      prerequisites: '',
      courseOverview: '',
    });
    setEditingId(null);
    setAiTopic('');
    setAiPrompt('');
    setAiMessage(null);
    setShowForm(true);
  };

  const handleAiGenerateProgram = async () => {
    const topic = (aiTopic || form.display_Title || form.title || '').trim();
    if (!topic) {
      setAiMessage('Enter a program title or AI topic first.');
      return;
    }
    setAiGenerating(true);
    setAiMessage(null);
    try {
      const response = await contentApi.generate({
        topic,
        prompt:
          aiPrompt.trim() ||
          'Create an IDC Academy mentor-led program curriculum with clear modules and topics suitable for a multi-month engineering program.',
        generationType: 'course',
        action: 'generate',
        difficulty: 'Intermediate',
      });
      if (!response.data.success || !response.data.content) {
        setAiMessage(response.data.message || 'AI generation failed.');
        return;
      }
      const generated = response.data.content;
      const highlights = (generated.sections || []).map((section, index) => ({
        module: `${index + 1}. ${section.heading}`,
        topics: (section.blocks || [])
          .map((block) => (block.content || '').trim())
          .filter(Boolean)
          .slice(0, 12),
      }));
      const contentPayload = {
        program: {
          title: generated.title || topic,
          duration: generated.duration || form.duration || '6 Months',
          programHighlights: highlights,
          learningJourney: (generated.keyTakeaways || []).slice(0, 8),
          outcomes: generated.learningOutcomes?.length
            ? generated.learningOutcomes
            : generated.keyTakeaways || [],
          projects: [],
        },
      };
      setForm((current) => ({
        ...current,
        title: current.title || generated.title || topic,
        display_Title: current.display_Title || generated.title || topic,
        courseOverview:
          current.courseOverview ||
          generated.objective ||
          generated.subtitle ||
          current.courseOverview,
        prerequisites: current.prerequisites || generated.prerequisites || current.prerequisites,
        duration: current.duration || generated.duration || current.duration,
        content: JSON.stringify(contentPayload, null, 2),
        courseMaterials: current.courseMaterials || JSON.stringify(generated.keyTakeaways || [], null, 2),
      }));
      setAiMessage('AI drafted overview, curriculum JSON, and materials. Review and edit before saving.');
    } catch (err: unknown) {
      setAiMessage(err instanceof Error ? err.message : 'AI generation failed.');
    } finally {
      setAiGenerating(false);
    }
  };
  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setProgramFormMessage(null);
    setProgramFormError(null);
    try {
      if (editingId) {
        const response = await fetch(apiUrl(API_PATHS.courses.update(editingId)), {
          method: 'PUT',
          headers: apiHeaders(token, { 'Content-Type': 'application/json' }),
          body: JSON.stringify({ course: form }),
        });
        const data = await response.json();
        if (data.success) {
          setCourses(courses.map(c => (c.id === editingId ? { ...c, ...form } as Course : c)));
          setProgramFormMessage('Program updated.');
        } else {
          setProgramFormError(data.message || 'Failed to update program.');
        }
      } else {
        const response = await fetch(apiUrl(API_PATHS.courses.create), {
          method: 'POST',
          headers: apiHeaders(token, { 'Content-Type': 'application/json' }),
          body: JSON.stringify({ course: form }),
        });
        const data = await response.json();
        if (data.success) {
          setCourses([...courses, data.course]);
          setProgramFormMessage('Program saved.');
          if (data.course?.id) setEditingId(data.course.id);
        } else {
          setProgramFormError(data.message || 'Failed to add program.');
        }
      }
    } catch {
      setProgramFormError('Failed to save program.');
    }
  };

  // Enrollments logic
  const fetchEnrollmentsForCourse = async (courseId: number) => {
    setLoadingEnrollments(true);
    setLoadingUserProfiles(false);
    try {
      const response = await fetch(apiUrl(API_PATHS.enrollments.byCourse(courseId)), {
        headers: apiHeaders(token),
      });
      const data = await response.json();
      const enrollments = data.courseEnrollments || [];
      setEnrollmentsByCourse(prev => ({ ...prev, [courseId]: enrollments }));
      // Batch fetch user details for all enrollments
      const userIds = enrollments.map((enr: any) => enr.userId).filter(Boolean);
      if (userIds.length > 0) {
        setLoadingUserProfiles(true);
        await fetchUserDetailsBatch(userIds);
        setLoadingUserProfiles(false);
      }
      // Pre-fetch course details (optional, but for completeness)
      fetchCourseDetails(courseId);
    } catch {
      setEnrollmentsByCourse(prev => ({ ...prev, [courseId]: [] }));
      setLoadingUserProfiles(false);
    } finally {
      setLoadingEnrollments(false);
    }
  };

  const handleViewEnrollments = (courseId: number) => {
    setSelectedCourseId(courseId);
    fetchEnrollmentsForCourse(courseId); // Always fetch fresh data
    setActiveTab('enrollments');
  };

  // Fetch enrollment counts for all courses
  const fetchEnrollmentCounts = async () => {
    setLoadingCounts(true);
    try {
      const response = await fetch(apiUrl(API_PATHS.courses.enrollmentCounts), {
        headers: apiHeaders(token),
      });
      const data = await response.json();
      if (data.success && data.courseEnrollmentCounts) {
        const countsMap: Record<number, { active: number; pending: number; total: number }> = {};
        data.courseEnrollmentCounts.forEach((item: any) => {
          countsMap[item.courseId] = {
            active: item.activeEnrollmentCount,
            pending: item.pendingEnrollmentCount,
            total: item.totalEnrollmentCount,
          };
        });
        setEnrollmentCounts(countsMap);
      }
    } catch (error) {
      console.error('Failed to fetch enrollment counts:', error);
    } finally {
      setLoadingCounts(false);
    }
  };

  // Refactored status change handler (edit only)
  const handleStatusEdit = (enrollmentId: number, status: string) => {
    setStatusEdits(prev => ({ ...prev, [enrollmentId]: status }));
    setSavedStatus(prev => ({ ...prev, [enrollmentId]: false }));
  };
  // Explicit save action
  const handleSaveStatus = async (courseId: number, enrollmentId: number) => {
    const status = statusEdits[enrollmentId];
    const userEmail = userDetails[enrollmentsByCourse[courseId]?.find((e: any) => e.id === enrollmentId)?.userId]?.email;
    setSavingStatus(prev => ({ ...prev, [enrollmentId]: true }));
    try {
      const response = await fetch(apiUrl(API_PATHS.enrollments.status(enrollmentId)), {
        method: 'PUT',
        headers: apiHeaders(token, { 'Content-Type': 'application/json' }),
        body: JSON.stringify({ enrollmentId, status, userEmail }),
      });
      const data = await response.json();
      if (data.success) {
        setEnrollmentsByCourse(prev => ({
          ...prev,
          [courseId]: prev[courseId].map((e: any) => e.id === enrollmentId ? { ...e, status } : e),
        }));
        setSavedStatus(prev => ({ ...prev, [enrollmentId]: true }));
        // Refresh enrollment counts after status change
        fetchEnrollmentCounts();
      } else {
        alert(data.message || 'Failed to update status.');
      }
    } catch {
      alert('Failed to update status.');
    } finally {
      setSavingStatus(prev => ({ ...prev, [enrollmentId]: false }));
    }
  };

  // User management handlers (mock)
  const handleEditUser = (user: any) => {
    alert(`Edit user: ${user.name}`);
  };
  const handleDeleteUser = async (id: number) => {
    const ok = await confirmAction({
      title: 'Delete this user?',
      description: 'This cannot be undone.',
      confirmLabel: 'Delete',
      cancelLabel: 'Keep user',
      variant: 'danger',
    });
    if (!ok) return;
    setUsers(users.filter(u => u.id !== id));
  };

  // Fetch inquiries when tab/page/pageSize changes
  useEffect(() => {
    if (activeTab !== 'inquiries') return;
    setInquiriesLoading(true);
    setInquiriesError(null);
    fetch(`${apiUrl(API_PATHS.inquiries.list)}?pageNumber=${inquiriesPage}&pageSize=${inquiriesPageSize}`, {
      headers: apiHeaders(token),
    })
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setInquiries(data.inquiries || []);
          setInquiriesTotal(data.totalCount || 0);
        } else {
          setInquiriesError(data.message || 'Failed to fetch inquiries');
        }
      })
      .catch(() => setInquiriesError('Failed to fetch inquiries'))
      .finally(() => setInquiriesLoading(false));
  }, [activeTab, inquiriesPage, inquiriesPageSize, token]);

  useEffect(() => {
    if (activeTab !== 'emails') return;
    setEmailNotificationsLoading(true);
    setEmailNotificationsError(null);
    const params = new URLSearchParams({
      pageNumber: String(emailNotificationsPage),
      pageSize: String(emailNotificationsPageSize),
    });
    if (emailStatusFilter) {
      params.set('status', emailStatusFilter);
    }
    fetch(`${apiUrl(API_PATHS.notifications.list)}?${params.toString()}`, {
      headers: apiHeaders(token),
    })
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setEmailNotifications(data.notifications || []);
          setEmailNotificationsTotal(data.totalCount || 0);
        } else {
          setEmailNotificationsError(data.message || 'Failed to fetch email notifications');
        }
      })
      .catch(() => setEmailNotificationsError('Failed to fetch email notifications'))
      .finally(() => setEmailNotificationsLoading(false));
  }, [activeTab, emailNotificationsPage, emailNotificationsPageSize, emailStatusFilter, token]);

  const handleRetriggerEmail = async (notificationId: number) => {
    setRetriggeringId(notificationId);
    setEmailNotificationsError(null);
    try {
      const response = await fetch(apiUrl(API_PATHS.notifications.retrigger(notificationId)), {
        method: 'POST',
        headers: apiHeaders(token),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        setEmailNotificationsError(data.message || 'Failed to retrigger email');
        return;
      }
      setEmailNotificationsPage(1);
      setEmailStatusFilter('');
    } catch {
      setEmailNotificationsError('Failed to retrigger email');
    } finally {
      setRetriggeringId(null);
    }
  };

  // Mock reply action
  const handleReply = (inquiryId: number) => {
    setReplyingTo(inquiryId);
    setReplyMessage('');
    setTimeout(() => replyInputRef.current?.focus(), 100);
  };
  // Real reply action
  const handleSendReply = async (inquiryId: number) => {
    const inquiry = inquiries.find(i => i.id === inquiryId);
    if (!inquiry) return;
    setReplyLoading(true);
    setReplyError(null);
    setReplySuccess(null);
    try {
      const response = await fetch(apiUrl(API_PATHS.notifications.send), {
        method: 'POST',
        headers: apiHeaders(token, { 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          toEmail: inquiry.email,
          subject: inquiry.subject,
          body: replyMessage,
        }),
      });
      const data = await response.json();
      if (data.success) {
        setReplySuccess('Reply sent successfully.');
        setTimeout(() => {
          setReplyingTo(null);
          setReplyMessage('');
          setReplySuccess(null);
        }, 1500);
      } else {
        setReplyError(data.message || 'Failed to send reply.');
      }
    } catch {
      setReplyError('Failed to send reply.');
    } finally {
      setReplyLoading(false);
    }
  };
  const handleCancelReply = () => {
    setReplyingTo(null);
    setReplyMessage('');
  };

  // Sidebar options
  const sidebarOptions = isOwner
    ? [
        { key: 'adminstats', label: 'Admin Stats', icon: <Award className="h-5 w-5 mr-3" /> },
        { key: 'programs', label: 'Programs', icon: <BookOpen className="h-5 w-5 mr-3" /> },
        { key: 'interviews', label: 'Interviews', icon: <MessageSquare className="h-5 w-5 mr-3" /> },
        { key: 'users', label: 'Users', icon: <Users className="h-5 w-5 mr-3" /> },
        { key: 'gigs', label: 'Gigs', icon: <Briefcase className="h-5 w-5 mr-3" /> },
        { key: 'events', label: 'Events', icon: <Calendar className="h-5 w-5 mr-3" /> },
        { key: 'settings', label: 'Settings', icon: <Settings className="h-5 w-5 mr-3" /> },
        { key: 'inquiries', label: 'Inquiries', icon: <Mail className="h-5 w-5 mr-3" /> },
        { key: 'emails', label: 'Email Log', icon: <Mail className="h-5 w-5 mr-3" /> },
      ]
    : [
        { key: 'adminstats', label: 'Admin Stats', icon: <Award className="h-5 w-5 mr-3" /> },
        { key: 'programs', label: 'Programs', icon: <BookOpen className="h-5 w-5 mr-3" /> },
        { key: 'interviews', label: 'Interviews', icon: <MessageSquare className="h-5 w-5 mr-3" /> },
        { key: 'inquiries', label: 'Inquiries', icon: <Mail className="h-5 w-5 mr-3" /> },
        { key: 'emails', label: 'Email Log', icon: <Mail className="h-5 w-5 mr-3" /> },
      ];

  const fetchInterviews = async () => {
    setLoadingInterviews(true);
    setInterviewError(null);
    try {
      const [response, interviewerResponse] = await Promise.all([
        interviewApi.getAll(),
        interviewerApi.list(),
      ]);
      const items = response.data.interviewRequests || [];
      setInterviews(items);
      setFeedbackDrafts(Object.fromEntries(items.map((item) => [item.id, item.feedback || ''])));
      setInterviewers(interviewerResponse.data.interviewers || []);
      const userIds = items.map((item) => item.userProfileId).filter(Boolean);
      if (userIds.length > 0) {
        await fetchUserDetailsBatch(userIds);
      }
    } catch {
      setInterviewError('Failed to fetch interview requests.');
      setInterviews([]);
    } finally {
      setLoadingInterviews(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'interviews') {
      fetchInterviews();
    }
  }, [activeTab, token]);

  const openScheduleInterview = (item: InterviewRequestItem) => {
    setSchedulingInterview(item);
    setInterviewMeetingLink(item.meetingLink || '');
    setInterviewDate(item.scheduledDate ? item.scheduledDate.substring(0, 10) : '');
    setInterviewTime('');
    setInterviewScheduleSuccess(null);
    setInterviewError(null);
  };

  const handleScheduleInterview = async () => {
    if (!schedulingInterview) return;
    setSavingInterview(true);
    setInterviewError(null);
    try {
      let formattedTime = interviewTime;
      if (interviewTime && interviewTime.includes(':') && interviewTime.length <= 5) {
        const [hours, minutes] = interviewTime.split(':');
        const dateObj = new Date();
        dateObj.setHours(Number(hours), Number(minutes));
        formattedTime = dateObj.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true,
        }) + ' (IST)';
      }
      const user = userDetails[schedulingInterview.userProfileId];
      const response = await interviewApi.schedule(schedulingInterview.id, {
        meetingLink: interviewMeetingLink,
        scheduledDate: interviewDate,
        scheduledTime: formattedTime,
        userEmail: user?.email,
      });
      if (response.data.success) {
        setInterviewScheduleSuccess(response.data.message || 'Interview scheduled.');
        setSchedulingInterview(null);
        setInterviewMeetingLink('');
        setInterviewDate('');
        setInterviewTime('');
        await fetchInterviews();
      } else {
        setInterviewError(response.data.message || 'Failed to schedule interview.');
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to schedule interview.';
      setInterviewError(message);
    } finally {
      setSavingInterview(false);
    }
  };

  const matchingInterviewers = (technology: string, skills?: string) =>
    interviewers.filter((person) => {
      const techMatch = (person.technologies || []).some((tech) => tech.toLowerCase() === technology.toLowerCase());
      const skillHaystack = `${person.technicalSkills || ''} ${(person.technologies || []).join(' ')}`.toLowerCase();
      const skillMatch = !skills || skills.split(/[,/]/).some((part) => part.trim() && skillHaystack.includes(part.trim().toLowerCase()));
      return techMatch || skillMatch;
    });

  const handleAcceptInterest = async (item: InterviewRequestItem) => {
    setAssigningId(item.id);
    setInterviewError(null);
    try {
      const response = await interviewApi.acceptInterest(item.id);
      if (response.data.success) {
        setInterviewScheduleSuccess(response.data.message || 'Moved to Pending.');
        await fetchInterviews();
      } else {
        setInterviewError(response.data.message || 'Failed to accept interest.');
      }
    } catch (err: unknown) {
      setInterviewError(err instanceof Error ? err.message : 'Failed to accept interest.');
    } finally {
      setAssigningId(null);
    }
  };

  const handleAssignInterviewer = async (item: InterviewRequestItem, interviewerId: number) => {
    if (!interviewerId) return;
    setAssigningId(item.id);
    setInterviewError(null);
    try {
      const user = userDetails[item.userProfileId];
      const response = await interviewApi.assign(item.id, interviewerId, user?.email);
      if (response.data.success) {
        setInterviewScheduleSuccess(response.data.message || 'Interviewer assigned.');
        await fetchInterviews();
      } else {
        setInterviewError(response.data.message || 'Failed to assign interviewer.');
      }
    } catch (err: unknown) {
      setInterviewError(err instanceof Error ? err.message : 'Failed to assign interviewer.');
    } finally {
      setAssigningId(null);
    }
  };

  const handleRejectInterview = async () => {
    if (!rejectingInterview) return;
    setSavingInterview(true);
    setInterviewError(null);
    try {
      const user = userDetails[rejectingInterview.userProfileId];
      const response = await interviewApi.reject(rejectingInterview.id, rejectReason.trim() || undefined, user?.email);
      if (response.data.success) {
        setInterviewScheduleSuccess(response.data.message || 'Interview request rejected.');
        setRejectingInterview(null);
        setRejectReason('');
        await fetchInterviews();
      } else {
        setInterviewError(response.data.message || 'Failed to reject interview.');
      }
    } catch (err: unknown) {
      setInterviewError(err instanceof Error ? err.message : 'Failed to reject interview.');
    } finally {
      setSavingInterview(false);
    }
  };

  const handleInterviewFeedback = async (item: InterviewRequestItem, markCompleted: boolean) => {
    setSavingFeedbackId(item.id);
    setInterviewError(null);
    try {
      const response = await interviewApi.feedback(item.id, {
        feedback: (feedbackDrafts[item.id] || '').trim(),
        markCompleted,
      });
      if (response.data.success) {
        setInterviewScheduleSuccess(response.data.message || 'Feedback saved.');
        await fetchInterviews();
      } else {
        setInterviewError(response.data.message || 'Failed to save feedback.');
      }
    } catch (err: unknown) {
      setInterviewError(err instanceof Error ? err.message : 'Failed to save feedback.');
    } finally {
      setSavingFeedbackId(null);
    }
  };

  const handleDeleteInterview = async (item: InterviewRequestItem) => {
    const ok = await confirmAction({
      title: `Delete interview #${item.id}?`,
      description: 'This cannot be undone. The candidate will be notified.',
      confirmLabel: 'Delete',
      cancelLabel: 'Keep interview',
      variant: 'danger',
    });
    if (!ok) return;
    setDeletingId(item.id);
    setInterviewError(null);
    try {
      const user = userDetails[item.userProfileId];
      const response = await interviewApi.delete(item.id, user?.email);
      if (response.data.success) {
        setInterviewScheduleSuccess(response.data.message || 'Interview request deleted.');
        await fetchInterviews();
      } else {
        setInterviewError(response.data.message || 'Failed to delete interview.');
      }
    } catch (err: unknown) {
      setInterviewError(err instanceof Error ? err.message : 'Failed to delete interview.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleSendMeetingInvite = async (courseId: number) => {
    setSendingMeetingInvite(true);
    setMeetingInviteSuccess(null);
    setMeetingInviteError(null);
    try {
      // Format date: 'Thursday, 25 July 2025'
      let formattedDate = '';
      if (sessionDate) {
        const dateObj = new Date(sessionDate);
        formattedDate = dateObj.toLocaleDateString('en-GB', {
          weekday: 'long',
          day: '2-digit',
          month: 'long',
          year: 'numeric',
        });
      }
      // Format time: '10:00 AM (IST)'
      let formattedTime = '';
      if (sessionTime) {
        const [hours, minutes] = sessionTime.split(':');
        const dateObj = new Date();
        dateObj.setHours(Number(hours), Number(minutes));
        formattedTime = dateObj.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true,
        }) + ' (IST)';
      }
      const response = await fetch(apiUrl(API_PATHS.enrollments.sendInvite), {
        method: 'POST',
        headers: apiHeaders(token, { 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          courseId,
          meetingLink,
          sessionDate: formattedDate,
          sessionTime: formattedTime,
          type: meetingType,
        }),
      });
      if (response.ok) {
        const data = await response.json();
        setMeetingInviteSuccess(data.message || `Meeting invite sent to ${data.sentCount || 'participants'}.`);
      } else {
        const data = await response.json().catch(() => ({}));
        setMeetingInviteError(data.message || 'Failed to send meeting invite.');
      }
    } catch (err) {
      setMeetingInviteError('Failed to send meeting invite.');
    } finally {
      setSendingMeetingInvite(false);
      setTimeout(() => setMeetingInviteSuccess(null), 2000);
    }
  };

  return (
    <div className={embedded ? '' : 'flex h-screen bg-gray-100'}>
      {!embedded && isSidebarOpen && window.innerWidth < 1024 && (
        <div
          className="fixed inset-0 z-20 bg-black bg-opacity-40 transition-opacity lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
          aria-label="Close sidebar overlay"
        />
      )}
      {!embedded && (
      <aside
        ref={sidebarRef}
        className={`bg-white shadow-md fixed inset-y-0 left-0 z-30 w-64 transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        tabIndex={-1}
        aria-modal={isSidebarOpen && window.innerWidth < 1024 ? 'true' : undefined}
        role="dialog"
      >
        <div className="h-full flex flex-col">
          <div className="p-4 border-b flex items-center justify-between">
            <span className="text-xl font-bold text-primary-600">IDcoder Admin</span>
            <button
              className="lg:hidden p-2 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100"
              onClick={toggleSidebar}
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex-1 overflow-y-auto py-4">
            <ul className="space-y-1 px-4">
              {sidebarOptions.map(opt => (
                <li key={opt.key}>
                  <button
                    className={`flex items-center w-full px-4 py-2 rounded-md ${activeTab === opt.key ? 'bg-primary-100 text-primary-700 font-semibold' : 'hover:bg-gray-100 text-gray-700'}`}
                    onClick={() => setActiveTab(opt.key as any)}
                  >
                    {opt.icon}
                    {opt.label}
                  </button>
                </li>
              ))}
              <li className="mt-8">
                <button
                  onClick={logout}
                  className="w-full flex items-center px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                >
                  <X className="h-5 w-5 mr-3" />
                  Logout
                </button>
              </li>
            </ul>
          </nav>
          <div className="p-4 border-t">
            <div className="flex items-center">
              <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mr-3">
                <span className="font-semibold text-primary-700">
                  {user?.username?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'A'}
                </span>
              </div>
              <div>
                <p className="font-medium">{user?.username || user?.email?.split('@')[0]}</p>
                <p className="text-xs text-gray-500">{user?.email}</p>
              </div>
            </div>
          </div>
        </div>
      </aside>
      )}
      <main className={embedded ? '' : 'flex-1 overflow-x-hidden overflow-y-auto bg-gray-100'}>
        <div className={embedded ? '' : 'container mx-auto px-6 py-8'}>
          {!embedded && (
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-3xl font-semibold text-gray-800">Admin Dashboard</h1>
            <button
              className="lg:hidden p-2 rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-200"
              onClick={toggleSidebar}
            >
              <Menu className="h-6 w-6" />
            </button>
          </div>
          )}

          {/* Admin Stats Tab */}
          {activeTab === 'adminstats' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {[
                { title: 'Total Users', value: 256, icon: <Users className="h-6 w-6 text-primary-600" /> },
                { title: 'Active Programs', value: 4, icon: <BookOpen className="h-6 w-6 text-primary-600" /> },
                { title: 'Current Enrollments', value: 187, icon: <Award className="h-6 w-6 text-primary-600" /> },
                { title: 'Upcoming Cohorts', value: 3, icon: <Calendar className="h-6 w-6 text-primary-600" /> },
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <Card className="p-6">
                    <div className="flex items-center">
                      <div className="p-3 rounded-full bg-primary-100 mr-4">
                        {stat.icon}
                      </div>
                      <div>
                        <p className="text-gray-500 text-sm">{stat.title}</p>
                        <p className="text-2xl font-semibold text-gray-800">{stat.value}</p>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
          {/* Programs Tab */}
          {activeTab === 'programs' && (
            <>
              <div className="mb-6 flex justify-between items-center">
                <h2 className="text-xl font-semibold">Programs</h2>
                <Button onClick={handleAdd}>Add Program</Button>
              </div>

              {loadingCourses ? (
                <div className="flex justify-center items-center min-h-[100px]">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600"></div>
                </div>
              ) : errorCourses ? (
                <div className="text-center text-red-600">{errorCourses}</div>
              ) : (
                <div className="grid gap-4 mb-8">
                  {courses.map(course => {
                    const counts = enrollmentCounts[course.id] || { active: 0, pending: 0, total: 0 };
                    return (
                      <Card key={course.id} className="p-4 flex flex-col md:flex-row md:items-center md:justify-between">
                        <div className="flex-1">
                          <div className="font-bold text-lg">{course.display_Title || course.title}</div>
                          <div className="text-gray-600">Start: {formatDate(course.startDate)}</div>
                          <div className="text-gray-600">Status: {course.isActive ? 'Active' : 'Inactive'}</div>
                          {/* Enrollment counts */}
                          <div className="mt-2 flex gap-4">
                            {loadingCounts ? (
                              <div className="flex items-center gap-2 text-sm text-gray-500">
                                <div className="animate-spin rounded-full h-3 w-3 border-t-2 border-b-2 border-primary-600"></div>
                                Loading enrollments...
                              </div>
                            ) : (
                              <>
                                <div className="flex items-center gap-1">
                                  <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                                  <span className="text-sm text-gray-600">Pending: {counts.pending}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                                  <span className="text-sm text-gray-600">Active: {counts.active}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                                  <span className="text-sm text-gray-600">Total: {counts.total}</span>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 mt-4 md:mt-0">
                          <Button variant="outline" onClick={() => handleEdit(course)}>Edit</Button>
                          <Button variant="outline" onClick={() => handleDelete(course.id)}>Deactivate</Button>
                          {isOwner && (
                            <Button variant="outline" onClick={() => handlePermanentDelete(course.id)}>Delete permanently</Button>
                          )}
                          <Button onClick={() => handleViewEnrollments(course.id)}>View Enrollments</Button>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              )}
              {showForm && (
                <Card className="p-6 mb-8">
                  <h3 className="text-lg font-semibold mb-4">{editingId ? 'Edit Program' : 'Add Program'}</h3>
                  <div className="mb-4 rounded-xl border border-indigo-100 bg-indigo-50/60 p-4 space-y-3">
                    <p className="text-sm font-semibold text-indigo-900">AI assist — sections &amp; curriculum</p>
                    <input
                      className="border p-2 rounded w-full bg-white"
                      placeholder="AI topic (defaults to title)"
                      value={aiTopic}
                      onChange={(e) => setAiTopic(e.target.value)}
                    />
                    <textarea
                      className="border p-2 rounded w-full bg-white min-h-[72px]"
                      placeholder="Optional guidance for curriculum focus, stack, duration…"
                      value={aiPrompt}
                      onChange={(e) => setAiPrompt(e.target.value)}
                    />
                    <div className="flex flex-wrap items-center gap-2">
                      <Button type="button" onClick={handleAiGenerateProgram} disabled={aiGenerating || !canManagePrograms}>
                        {aiGenerating ? 'Generating…' : 'Generate with AI'}
                      </Button>
                      {aiMessage ? <span className="text-sm text-indigo-900">{aiMessage}</span> : null}
                    </div>
                  </div>
                  <form onSubmit={handleFormSubmit} className="grid gap-4 grid-cols-1 md:grid-cols-2">
                    <input className="border p-2 rounded" placeholder="Title" value={form.title || ''} onChange={e => setForm(f => ({ ...f, title: e.target.value, display_Title: e.target.value }))} required />
                    <input className="border p-2 rounded" placeholder="Display title" value={form.display_Title || ''} onChange={e => setForm(f => ({ ...f, display_Title: e.target.value }))} />
                    <input className="border p-2 rounded" placeholder="Start Date" type="date" value={(form.startDate || '').toString().slice(0, 10)} onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))} required />
                    <input className="border p-2 rounded" placeholder="Last date for enrollment" type="date" value={(form.lastDateForEnrollment || '').toString().slice(0, 10)} onChange={e => setForm(f => ({ ...f, lastDateForEnrollment: e.target.value }))} />
                    <input className="border p-2 rounded" placeholder="Category" value={form.category || ''} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} />
                    <input className="border p-2 rounded" placeholder="Subcategory" value={form.subcategory || ''} onChange={e => setForm(f => ({ ...f, subcategory: e.target.value }))} />
                    <input className="border p-2 rounded" placeholder="Duration" value={form.duration || ''} onChange={e => setForm(f => ({ ...f, duration: e.target.value }))} />
                    <select className="border p-2 rounded" value={form.trainingMode || 'Online'} onChange={e => setForm(f => ({ ...f, trainingMode: e.target.value }))}>
                      <option value="Online">Online</option>
                      <option value="Offline">Offline</option>
                    </select>
                    <input className="border p-2 rounded" placeholder="Fee" type="number" value={form.fee ?? ''} onChange={e => setForm(f => ({ ...f, fee: Number(e.target.value) }))} />
                    <input className="border p-2 rounded" placeholder="Discount %" type="number" value={form.discount ?? ''} onChange={e => setForm(f => ({ ...f, discount: Number(e.target.value) }))} />
                    <input className="border p-2 rounded" placeholder="Max participants" type="number" value={form.maxParticipants ?? ''} onChange={e => setForm(f => ({ ...f, maxParticipants: Number(e.target.value) }))} />
                    <input className="border p-2 rounded" placeholder="Language" value={form.language || 'English'} onChange={e => setForm(f => ({ ...f, language: e.target.value }))} />
                    <select className="border p-2 rounded" value={form.isActive ? 'Active' : 'Inactive'} onChange={e => setForm(f => ({ ...f, isActive: e.target.value === 'Active' }))}>
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                    <textarea className="border p-2 rounded md:col-span-2 min-h-[88px]" placeholder="Course overview" value={form.courseOverview || ''} onChange={e => setForm(f => ({ ...f, courseOverview: e.target.value }))} />
                    <textarea className="border p-2 rounded md:col-span-2 min-h-[72px]" placeholder="Prerequisites" value={form.prerequisites || ''} onChange={e => setForm(f => ({ ...f, prerequisites: e.target.value }))} />
                    <textarea className="border p-2 rounded md:col-span-2 min-h-[72px]" placeholder="Tools (comma-separated or free text)" value={form.tools || ''} onChange={e => setForm(f => ({ ...f, tools: e.target.value }))} />
                    <textarea className="border p-2 rounded md:col-span-2 min-h-[160px] font-mono text-xs" placeholder="Content JSON (program highlights, journey, outcomes, projects)" value={form.content || ''} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} />
                    <textarea className="border p-2 rounded md:col-span-2 min-h-[88px] font-mono text-xs" placeholder="Course materials JSON array" value={form.courseMaterials || ''} onChange={e => setForm(f => ({ ...f, courseMaterials: e.target.value }))} />
                    <div className="col-span-2 mt-2">
                      <div className="flex gap-2">
                      <Button type="submit">Save</Button>
                      <Button variant="outline" type="button" onClick={async () => {
                        const ok = await confirmAction({
                          title: 'Discard program changes?',
                          description: 'Unsaved edits will be lost.',
                          confirmLabel: 'Discard',
                          cancelLabel: 'Keep editing',
                          variant: 'danger',
                        });
                        if (!ok) return;
                        setShowForm(false);
                        setEditingId(null);
                        setProgramFormMessage(null);
                        setProgramFormError(null);
                      }}>Cancel</Button>
                      </div>
                      <ActionStatus message={programFormMessage} error={programFormError} />
                    </div>
                  </form>
                </Card>
              )}
            </>
          )}
          {/* Interviews Tab */}
          {activeTab === 'interviews' && (
            <>
              <div className="mb-6 flex flex-col gap-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h2 className="text-xl font-semibold">Interviews</h2>
                  <Link to="/admin/interviews/new"><Button type="button">Schedule interview</Button></Link>
                </div>
                <div className="flex flex-col md:flex-row md:flex-wrap md:items-center gap-3">
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium">Status:</label>
                    <select
                      className="border p-2 rounded"
                      value={interviewStatusFilter}
                      onChange={(e) => setInterviewStatusFilter(e.target.value)}
                    >
                      <option value="All">All</option>
                      <option value="Interested">Interested</option>
                      <option value="Pending">Pending</option>
                      <option value="Scheduled">Scheduled</option>
                      <option value="Completed">Completed</option>
                      <option value="Cancelled">Cancelled</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium">Technology:</label>
                    <select
                      className="border p-2 rounded"
                      value={interviewTechFilter}
                      onChange={(e) => setInterviewTechFilter(e.target.value)}
                    >
                      <option value="All">All</option>
                      {INTERVIEW_TECHNOLOGIES.map((tech) => (
                        <option key={tech} value={tech}>{tech}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium">Interviewer:</label>
                    <select
                      className="border p-2 rounded"
                      value={interviewInterviewerFilter}
                      onChange={(e) => setInterviewInterviewerFilter(e.target.value)}
                    >
                      <option value="All">All</option>
                      {interviewers.map((person) => (
                        <option key={person.interviewerId} value={String(person.interviewerId)}>{person.fullName}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium">Order by date:</label>
                    <button
                      type="button"
                      className="px-3 py-2 border rounded text-sm"
                      onClick={() => setInterviewDateSort((value) => value === 'asc' ? 'desc' : 'asc')}
                    >
                      {interviewDateSort === 'asc' ? 'Oldest first' : 'Newest first'}
                    </button>
                  </div>
                </div>
                <AppliedFilters
                  items={[
                    ...(interviewStatusFilter !== 'All' ? [{ key: 'status', label: `Status: ${interviewStatusFilter}`, onRemove: () => setInterviewStatusFilter('All') }] : []),
                    ...(interviewTechFilter !== 'All' ? [{ key: 'tech', label: `Technology: ${interviewTechFilter}`, onRemove: () => setInterviewTechFilter('All') }] : []),
                    ...(interviewInterviewerFilter !== 'All' ? [{
                      key: 'interviewer',
                      label: `Interviewer: ${interviewers.find((person) => String(person.interviewerId) === interviewInterviewerFilter)?.fullName || interviewInterviewerFilter}`,
                      onRemove: () => setInterviewInterviewerFilter('All'),
                    }] : []),
                  ]}
                  onClear={() => {
                    setInterviewStatusFilter('All');
                    setInterviewTechFilter('All');
                    setInterviewInterviewerFilter('All');
                  }}
                />
              </div>
              {interviewError && !schedulingInterview && !rejectingInterview && (
                <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
                  {interviewError}
                </div>
              )}
              {loadingInterviews ? (
                <div className="flex justify-center items-center min-h-[100px]">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600"></div>
                </div>
              ) : (
                <div className="overflow-x-auto rounded-xl border bg-white">
                  {(() => {
                    const filtered = interviews
                      .filter((item) => interviewStatusFilter === 'All' || item.status === interviewStatusFilter)
                      .filter((item) => interviewTechFilter === 'All' || item.technology === interviewTechFilter)
                      .filter((item) => interviewInterviewerFilter === 'All' || String(item.interviewerId) === interviewInterviewerFilter)
                      .sort((a, b) => {
                        const dateA = new Date(a.requestDate).getTime();
                        const dateB = new Date(b.requestDate).getTime();
                        return interviewDateSort === 'asc' ? dateA - dateB : dateB - dateA;
                      });
                    if (filtered.length === 0) {
                      return <div className="text-center text-gray-500 py-8">No interview requests found.</div>;
                    }
                    return (
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                          <tr>
                            <th className="px-3 py-2">ID</th>
                            <th className="px-3 py-2">Candidate</th>
                            <th className="px-3 py-2">Interviewer</th>
                            <th className="px-3 py-2">Fee</th>
                            <th className="px-3 py-2">Requested</th>
                            <th className="px-3 py-2">Scheduled</th>
                            <th className="px-3 py-2">Status</th>
                            <th className="px-3 py-2"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {filtered.map((item) => {
                            const profile = userDetails[item.userProfileId] || {};
                            const candidate = item.candidateName || profile.username || profile.email || item.userProfileId;
                            return (
                              <tr key={item.id} className="border-t">
                                <td className="px-3 py-2 font-medium">#{item.id}</td>
                                <td className="px-3 py-2">{candidate}</td>
                                <td className="px-3 py-2">{item.interviewerName || '—'}</td>
                                <td className="px-3 py-2">{item.price}</td>
                                <td className="px-3 py-2">{formatDate(item.requestDate)}</td>
                                <td className="px-3 py-2">{item.scheduledDate ? `${formatDate(item.scheduledDate)} ${item.scheduledTime || ''}` : '—'}</td>
                                <td className="px-3 py-2">
                                  <span className={`inline-flex items-center gap-1 font-semibold ${item.status === 'Completed' ? 'text-emerald-700' : 'text-gray-800'}`}>
                                    {item.status === 'Completed' && <CheckCircle className="h-4 w-4" />}
                                    {item.status}
                                  </span>
                                </td>
                                <td className="px-3 py-2">
                                  <div className="flex flex-wrap gap-2">
                                    {item.status === 'Interested' && (
                                      <Button
                                        size="sm"
                                        disabled={assigningId === item.id}
                                        onClick={() => handleAcceptInterest(item)}
                                      >
                                        {assigningId === item.id ? '…' : 'Move to pending'}
                                      </Button>
                                    )}
                                    <Link to={`/interviews/${item.id}`}><Button size="sm" variant="outline">Open</Button></Link>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    );
                  })()}
                </div>
              )}
            </>
          )}
          {/* Enrollments Tab */}
          {activeTab === 'enrollments' && (
            <>
              <h2 className="text-xl font-semibold mb-4">Enrollments</h2>
              {selectedCourseId ? (
                <Card className="p-6 mb-8">
                  <div className="flex flex-row items-center justify-between mb-4">
                    <h3 className="text-xl font-bold">{courseDetails[selectedCourseId]?.title || ''}</h3>
                    <div className="flex gap-2 items-center">
                      <Button
                        variant="primary"
                        onClick={() => setShowMeetingModal(true)}
                      >
                        Send Meeting Invite
                      </Button>
                      {meetingInviteSuccess && (
                        <span className="text-green-600 text-sm ml-2">{meetingInviteSuccess}</span>
                      )}
                      {meetingInviteError && (
                        <span className="text-red-600 text-sm ml-2">{meetingInviteError}</span>
                      )}
                      <Button variant="outline" onClick={() => { setSelectedCourseId(null); setActiveTab('programs'); }} className="ml-4">Move to Programs</Button>
                    </div>
                  </div>
                  {/* Show important course fields */}
                  {courseDetails[selectedCourseId] && (
                    <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <span className="font-semibold">Start Date: </span>
                        {formatDate(courseDetails[selectedCourseId].startDate)}
                      </div>
                      <div>
                        <span className="font-semibold">Last Date for Enrollment: </span>
                        {formatDate(courseDetails[selectedCourseId].lastDateForEnrollment)}
                      </div>
                      <div>
                        <span className="font-semibold">Category: </span>
                        {courseDetails[selectedCourseId].category}
                      </div>
                      <div>
                        <span className="font-semibold">Fee: </span>
                        {courseDetails[selectedCourseId].fee}
                      </div>
                    </div>
                  )}
                  {loadingEnrollments || loadingUserProfiles ? (
                    <div className="flex justify-center items-center min-h-[100px]">
                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600"></div>
                    </div>
                  ) : (
                    <>
                      {/* Filter and Sort Controls */}
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                        <div className="flex items-center gap-2">
                          <label className="font-medium text-sm">Filter by Status:</label>
                          <select
                            className="border p-2 rounded"
                            value={enrollmentStatusFilter}
                            onChange={e => setEnrollmentStatusFilter(e.target.value)}
                          >
                            <option value="All">All</option>
                            {STATUS_OPTIONS.map(opt => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        </div>
                        <div className="flex items-center gap-2">
                          <label className="font-medium text-sm">Sort by:</label>
                          <select
                            className="border p-2 rounded"
                            value={enrollmentSort.field}
                            onChange={e => setEnrollmentSort(s => ({ ...s, field: e.target.value as 'status' | 'enrollmentDate' }))}
                          >
                            <option value="enrollmentDate">Enrolled Date</option>
                            <option value="status">Status</option>
                          </select>
                          <button
                            className="ml-2 px-2 py-1 border rounded text-sm"
                            onClick={() => setEnrollmentSort(s => ({ ...s, direction: s.direction === 'asc' ? 'desc' : 'asc' }))}
                          >
                            {enrollmentSort.direction === 'asc' ? 'Asc' : 'Desc'}
                          </button>
                        </div>
                      </div>
                      {/* Vertical List of Enrollments */}
                      <div className="flex flex-col gap-3">
                        {(() => {
                          let filtered = enrollmentsByCourse[selectedCourseId] || [];
                          if (enrollmentStatusFilter !== 'All') {
                            filtered = filtered.filter(e => e.status === enrollmentStatusFilter);
                          }
                          filtered = [...filtered].sort((a, b) => {
                            if (enrollmentSort.field === 'status') {
                              if (a.status < b.status) return enrollmentSort.direction === 'asc' ? -1 : 1;
                              if (a.status > b.status) return enrollmentSort.direction === 'asc' ? 1 : -1;
                              return 0;
                            } else {
                              // enrollmentDate
                              const dateA = new Date(a.enrollmentDate).getTime();
                              const dateB = new Date(b.enrollmentDate).getTime();
                              return enrollmentSort.direction === 'asc' ? dateA - dateB : dateB - dateA;
                            }
                          });
                          return filtered.map(enr => {
                            const isEdited = statusEdits[enr.id] && statusEdits[enr.id] !== enr.status;
                            const user = userDetails[enr.userId] || {};
                            const initials = (user.username || user.email || '').split(' ').map((n: string) => n[0]).join('').substring(0,2).toUpperCase();
                            const statusColorMap: Record<string, string> = {
                              'Active': 'bg-green-100 text-green-800',
                              'Completed': 'bg-blue-100 text-blue-800',
                              'Pending': 'bg-yellow-100 text-yellow-800',
                              'Cancelled': 'bg-red-100 text-red-800',
                              'Inactive': 'bg-gray-100 text-gray-800',
                            };
                            const statusColor = statusColorMap[String(enr.status)] || 'bg-gray-100 text-gray-800';
                            return (
                              <Card
                                key={enr.id}
                                className={`flex flex-col md:flex-row md:items-center gap-2 md:gap-4 px-4 py-3 shadow transition hover:shadow-lg border ${isEdited ? 'border-yellow-400 bg-yellow-50' : 'border-gray-200 bg-white'}`}
                              >
                                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-base font-bold text-primary-700 mb-2 md:mb-0">
                                  {initials || '?'}
                                </div>
                                <div className="flex-1 min-w-0 w-full">
                                  <div className="font-semibold text-gray-900 truncate">{user.username || user.email || enr.userId}</div>
                                  <div className="text-sm text-gray-600 truncate">{user.email}</div>
                                  <div className="text-sm text-gray-600 truncate">{user.mobile}</div>
                                </div>
                                <div className="flex flex-col items-end md:items-end gap-1 min-w-[120px] w-full md:w-auto">
                                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusColor}`}>{enr.status}</span>
                                  <span className="text-xs text-gray-500">{formatDate(enr.enrollmentDate)}</span>
                                </div>
                                <div className="flex items-center gap-2 ml-0 md:ml-4 w-full md:w-auto mt-2 md:mt-0">
                                  <select
                                    value={statusEdits[enr.id] ?? enr.status}
                                    onChange={e => handleStatusEdit(enr.id, e.target.value)}
                                    className="border p-1 rounded w-full md:w-auto"
                                    disabled={savingStatus[enr.id]}
                                  >
                                    {STATUS_OPTIONS.map(opt => (
                                      <option key={opt} value={opt}>{opt}</option>
                                    ))}
                                  </select>
                                  {isEdited && (
                                    <Button
                                      size="sm"
                                      variant="primary"
                                      onClick={() => handleSaveStatus(selectedCourseId, enr.id)}
                                      disabled={savingStatus[enr.id]}
                                    >
                                      {savingStatus[enr.id] ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                      ) : (
                                        <Check className="h-4 w-4" />
                                      )}
                                    </Button>
                                  )}
                                  {savedStatus[enr.id] && !isEdited && (
                                    <Check className="h-4 w-4 text-green-600 ml-1" />
                                  )}
                                </div>
                              </Card>
                            );
                          });
                        })()}
                      </div>
                    </>
                  )}
                </Card>
              ) : (
                <div className="grid gap-4 mb-8">
                  {courses.map(course => (
                    <Card key={course.id} className="p-4 flex flex-col md:flex-row md:items-center md:justify-between">
                      <div>
                        <div className="font-bold text-lg">{course.display_Title || course.title}</div>
                        <div className="text-gray-600">Start: {formatDate(course.startDate)}</div>
                        <div className="text-gray-600">Status: {course.isActive ? 'Active' : 'Inactive'}</div>
                      </div>
                      <div className="flex gap-2 mt-4 md:mt-0">
                        <Button onClick={() => handleViewEnrollments(course.id)}>View Enrollments</Button>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </>
          )}
          {/* Users Tab (Owner only) */}
          {activeTab === 'users' && isOwner && (
            <>
              <h2 className="text-xl font-semibold mb-4">Users</h2>
              <div className="grid gap-4 mb-8">
                {users.map(u => (
                  <Card key={u.id} className="p-4 flex flex-col md:flex-row md:items-center md:justify-between">
                    <div>
                      <div className="font-bold text-lg">{u.name}</div>
                      <div className="text-gray-600">{u.email}</div>
                      <div className="text-gray-600">Role: {u.role}</div>
                    </div>
                    <div className="flex gap-2 mt-4 md:mt-0">
                      <Button variant="outline" onClick={() => handleEditUser(u)}>Edit</Button>
                      <Button variant="outline" onClick={() => handleDeleteUser(u.id)}>Delete</Button>
                    </div>
                  </Card>
                ))}
              </div>
            </>
          )}
          {/* Gigs Tab (Owner only) */}
          {activeTab === 'gigs' && isOwner && (
            <>
              <h2 className="text-xl font-semibold mb-4">Gig Management</h2>
              <Card className="p-6 mb-8">Gig management features coming soon.</Card>
            </>
          )}
          {/* Events Tab (Owner only) */}
          {activeTab === 'events' && isOwner && (
            <>
              <h2 className="text-xl font-semibold mb-4">Event Management</h2>
              <Card className="p-6 mb-8">Event management features coming soon.</Card>
            </>
          )}
          {/* Settings Tab (Owner only) */}
          {activeTab === 'settings' && isOwner && (
            <>
              <h2 className="text-xl font-semibold mb-4">Settings</h2>
              <Card className="p-6 mb-8">Settings panel (Owner only)</Card>
            </>
          )}
          {/* Inquiries Tab */}
          {activeTab === 'inquiries' && (
            <div>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-2">
                <h2 className="text-xl font-bold">Contact Inquiries</h2>
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium">Page Size:</label>
                  <select
                    className="border p-1 rounded"
                    value={inquiriesPageSize}
                    onChange={e => { setInquiriesPageSize(Number(e.target.value)); setInquiriesPage(1); }}
                  >
                    {[5, 10, 20, 50].map(size => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                </div>
              </div>
              {inquiriesLoading ? (
                <div className="flex justify-center items-center min-h-[100px]">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600"></div>
                </div>
              ) : inquiriesError ? (
                <div className="text-center text-red-600">{inquiriesError}</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 border">
                     <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Subject</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Message</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {inquiries.map(inq => (
                        <tr key={inq.id}>
                          <td className="px-4 py-2 font-semibold text-gray-900">{inq.name}</td>
                          <td className="px-4 py-2 text-sm text-gray-600">{inq.email}</td>
                          <td className="px-4 py-2 text-sm text-gray-600">{inq.subject}</td>
                          <td className="px-4 py-2 text-sm text-gray-600">{inq.contactType}</td>
                          <td className="px-4 py-2 text-xs text-gray-500">{formatDate(inq.createdDate)}</td>
                          <td className="px-4 py-2 text-gray-800 max-w-xs break-words">{inq.message}</td>
                          <td className="px-4 py-2">
                            {replyingTo === inq.id ? (
                              <div className="flex flex-col gap-2 w-64">
                                <textarea
                                  ref={replyInputRef}
                                  className="border p-2 rounded w-full min-w-[180px] min-h-[100px]"
                                  rows={5}
                                  value={replyMessage}
                                  onChange={e => setReplyMessage(e.target.value)}
                                  placeholder="Type your reply..."
                                  disabled={replyLoading}
                                />
                                {replyLoading && <div className="text-xs text-blue-600">Sending...</div>}
                                {replySuccess && <div className="text-xs text-green-600">{replySuccess}</div>}
                                {replyError && <div className="text-xs text-red-600">{replyError}</div>}
                                <div className="flex gap-2">
                                  <Button size="sm" variant="primary" onClick={() => handleSendReply(inq.id)} disabled={!replyMessage.trim() || replyLoading}>Send</Button>
                                  <Button size="sm" variant="outline" onClick={handleCancelReply} disabled={replyLoading}>Cancel</Button>
                                </div>
                              </div>
                            ) : (
                              <Button size="sm" variant="outline" onClick={() => handleReply(inq.id)}>Reply</Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {/* Pagination */}
                  <div className="flex items-center justify-between mt-4">
                    <div className="text-sm text-gray-600">
                      Showing {inquiries.length} of {inquiriesTotal} inquiries
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" disabled={inquiriesPage === 1} onClick={() => setInquiriesPage(p => Math.max(1, p - 1))}>Previous</Button>
                      <span className="text-sm">Page {inquiriesPage}</span>
                      <Button size="sm" variant="outline" disabled={inquiriesPage * inquiriesPageSize >= inquiriesTotal} onClick={() => setInquiriesPage(p => p + 1)}>Next</Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          {/* Email Notifications Tab */}
          {activeTab === 'emails' && (
            <div>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-2">
                <h2 className="text-xl font-bold">Email Notifications</h2>
                <div className="flex items-center gap-2 flex-wrap">
                  <label className="text-sm font-medium">Status:</label>
                  <select
                    className="border p-1 rounded"
                    value={emailStatusFilter}
                    onChange={e => { setEmailStatusFilter(e.target.value); setEmailNotificationsPage(1); }}
                  >
                    <option value="">All</option>
                    <option value="Failed">Failed</option>
                    <option value="Sent">Sent</option>
                    <option value="Pending">Pending</option>
                  </select>
                  <label className="text-sm font-medium">Page Size:</label>
                  <select
                    className="border p-1 rounded"
                    value={emailNotificationsPageSize}
                    onChange={e => { setEmailNotificationsPageSize(Number(e.target.value)); setEmailNotificationsPage(1); }}
                  >
                    {[10, 20, 50].map(size => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                </div>
              </div>
              {emailNotificationsError && (
                <div className="mb-3 text-sm text-red-600">{emailNotificationsError}</div>
              )}
              {emailNotificationsLoading ? (
                <div className="flex justify-center items-center min-h-[100px]">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600"></div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 border text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">To</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Subject</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Retries</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Error</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {emailNotifications.map((item) => (
                        <tr key={item.id}>
                          <td className="px-3 py-2">{item.notificationType}</td>
                          <td className="px-3 py-2">{item.toEmail}</td>
                          <td className="px-3 py-2 max-w-xs truncate">{item.subject}</td>
                          <td className="px-3 py-2">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              item.status === 'Sent' ? 'bg-green-100 text-green-800' :
                              item.status === 'Failed' ? 'bg-red-100 text-red-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              {item.status}
                            </span>
                          </td>
                          <td className="px-3 py-2">{item.retryCount}</td>
                          <td className="px-3 py-2">{formatDate(item.createdDate)}</td>
                          <td className="px-3 py-2 max-w-xs truncate text-red-600">{item.errorMessage || '—'}</td>
                          <td className="px-3 py-2">
                            {item.status === 'Failed' && (
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={retriggeringId === item.id}
                                onClick={() => handleRetriggerEmail(item.id)}
                              >
                                {retriggeringId === item.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <>
                                    <RefreshCw className="h-4 w-4 mr-1" />
                                    Retrigger
                                  </>
                                )}
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                      {emailNotifications.length === 0 && (
                        <tr>
                          <td colSpan={8} className="px-3 py-6 text-center text-gray-500">No email notifications found.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  <div className="flex justify-between items-center mt-4">
                    <span className="text-sm text-gray-600">Total: {emailNotificationsTotal}</span>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" disabled={emailNotificationsPage === 1} onClick={() => setEmailNotificationsPage(p => Math.max(1, p - 1))}>Previous</Button>
                      <span className="text-sm">Page {emailNotificationsPage}</span>
                      <Button size="sm" variant="outline" disabled={emailNotificationsPage * emailNotificationsPageSize >= emailNotificationsTotal} onClick={() => setEmailNotificationsPage(p => p + 1)}>Next</Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
      {/* Meeting Invite Modal */}
      {showMeetingModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40" onMouseDown={(event) => event.stopPropagation()} onClick={(event) => event.stopPropagation()}>
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md relative">
            <button className="absolute top-2 right-2 text-gray-500 hover:text-gray-700" onClick={() => setShowMeetingModal(false)}>&times;</button>
            <h4 className="text-lg font-semibold mb-4">Send Meeting Invite</h4>
            <form onSubmit={e => { e.preventDefault(); if (selectedCourseId !== null) handleSendMeetingInvite(selectedCourseId); }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Meeting Link</label>
                <input type="text" className="w-full border p-2 rounded" value={meetingLink} onChange={e => setMeetingLink(e.target.value)} required />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Session Date</label>
                <Input type="date" value={sessionDate} onChange={e => setSessionDate(e.target.value)} required />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Session Time</label>
                <Input type="time" value={sessionTime} onChange={e => setSessionTime(e.target.value)} required />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Type</label>
                <select className="w-full border p-2 rounded" value={meetingType} onChange={e => setMeetingType(e.target.value as 'Invite' | 'Reminder')} required>
                  <option value="Invite">Invite</option>
                  <option value="Reminder">Reminder</option>
                </select>
              </div>
              <div className="mt-4">
                <div className="flex gap-2">
                <Button type="submit" variant="primary" disabled={sendingMeetingInvite || selectedCourseId === null}>{sendingMeetingInvite ? 'Sending...' : 'Send Invite'}</Button>
                <Button type="button" variant="outline" onClick={async () => {
                  const ok = await confirmAction({
                    title: 'Discard this invite?',
                    description: 'The meeting details you entered will not be sent.',
                    confirmLabel: 'Discard',
                    cancelLabel: 'Keep editing',
                    variant: 'danger',
                  });
                  if (!ok) return;
                  setShowMeetingModal(false);
                }} disabled={sendingMeetingInvite}>Cancel</Button>
                </div>
                <ActionStatus message={meetingInviteSuccess} error={meetingInviteError} />
              </div>
            </form>
          </div>
        </div>
      )}
      {schedulingInterview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40" onMouseDown={(event) => event.stopPropagation()} onClick={(event) => event.stopPropagation()}>
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md relative">
            <button className="absolute top-2 right-2 text-gray-500 hover:text-gray-700" onClick={() => setSchedulingInterview(null)}>&times;</button>
            <h4 className="text-lg font-semibold mb-1">Confirm & schedule interview #{schedulingInterview.id}</h4>
            <p className="text-sm text-gray-600 mb-4">
              {schedulingInterview.otherTechnology || schedulingInterview.technology}
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleScheduleInterview();
              }}
              className="space-y-4"
            >
              <div>
                <label className="block text-sm font-medium mb-1">Meeting Link</label>
                <input
                  type="url"
                  className="w-full border p-2 rounded"
                  value={interviewMeetingLink}
                  onChange={(e) => setInterviewMeetingLink(e.target.value)}
                  placeholder="https://meet.google.com/..."
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Interview Date</label>
                <Input type="date" value={interviewDate} onChange={(e) => setInterviewDate(e.target.value)} required />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Interview Time</label>
                <Input type="time" value={interviewTime} onChange={(e) => setInterviewTime(e.target.value)} required />
              </div>
              <div className="mt-4">
                <div className="flex gap-2">
                <Button type="submit" variant="primary" disabled={savingInterview}>
                  {savingInterview ? 'Saving...' : 'Confirm & schedule'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setSchedulingInterview(null)} disabled={savingInterview}>
                  Cancel
                </Button>
                </div>
                <ActionStatus message={interviewScheduleSuccess} error={interviewError && schedulingInterview ? interviewError : null} />
              </div>
            </form>
          </div>
        </div>
      )}
      {rejectingInterview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40" onMouseDown={(event) => event.stopPropagation()} onClick={(event) => event.stopPropagation()}>
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md relative">
            <button className="absolute top-2 right-2 text-gray-500 hover:text-gray-700" onClick={() => setRejectingInterview(null)}>&times;</button>
            <h4 className="text-lg font-semibold mb-1">Reject interview #{rejectingInterview.id}</h4>
            <p className="text-sm text-gray-600 mb-4">
              {rejectingInterview.otherTechnology || rejectingInterview.technology}
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleRejectInterview();
              }}
              className="space-y-4"
            >
              <div>
                <label className="block text-sm font-medium mb-1">Note to candidate (optional)</label>
                <textarea
                  className="w-full border p-2 rounded"
                  rows={3}
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="We'll include this in the email if provided."
                />
              </div>
              <div className="flex gap-2 mt-4">
                <Button type="submit" variant="primary" disabled={savingInterview}>
                  {savingInterview ? 'Rejecting...' : 'Reject request'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setRejectingInterview(null)} disabled={savingInterview}>
                  Cancel
                </Button>
              </div>
              {interviewError && <div className="text-red-600 text-sm mt-2">{interviewError}</div>}
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SchoolAdminPanel; 